import React from 'react';
import { Modal, Box, Typography, IconButton, Grid, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BlockIcon from '@mui/icons-material/Block';
import StorageIcon from '@mui/icons-material/Storage';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelIcon from '@mui/icons-material/Label';
import { useTranslation } from 'react-i18next';
import './home/Disparador';

const TriggerDetailsModal = ({ open, onClose, details }) => {
    const { t } = useTranslation();
    const {
        failure_count,
        success_count,
        resulted,
        email,
        responsible_user_name,
        user_name,
        interrupted_by_user_name,
        bot_name,
        entry_bot_legacy_id,
        bot_legacy_id,
        bot_id,
        filter_file_type_input,
        filter_file_date_interaction,
        filter_file_tag_name,
    } = details || {};
    const responsibleName = responsible_user_name || user_name || '-';
    const interruptedByName = interrupted_by_user_name || '-';
    const botLegacyId = entry_bot_legacy_id || bot_legacy_id || bot_id;
    const triggeredBotName = bot_name || (botLegacyId ? `${botLegacyId} ${t('disparador.deletedSuffix')}` : '-');

    // Map valores traduzidos de volta para chaves de tradução para exibição
    const getFileInputTypeTranslationKey = (value) => {
        if (!value) return null;
        
        // Se o valor recebido for exatamente uma das traduções, detecta e retorna a chave
        const spreadsheetTranslations = [
            t('disparador.fileTypeSpreadsheet'),
            'Csv/EXEL',
            'CSV/EXEL',
            'csv/exel',
            'Spreadsheet',
            'CSV/Excel'
        ];
        
        const tagTranslations = [
            t('disparador.fileTypeTag'),
            'Etiqueta',
            'etiqueta',
            'Tag',
            'tag'
        ];
        
        const filterTranslations = [
            t('disparador.fileTypeFilter'),
            'Interação',
            'intera\u00e7\u00e3o',
            'Interaction',
            'interaction',
            'Interacci\u00f3n',
            'interacci\u00f3n'
        ];
        
        if (spreadsheetTranslations.some(trans => trans.toLowerCase() === value.toLowerCase())) {
            return 'disparador.fileTypeSpreadsheet';
        }
        if (tagTranslations.some(trans => trans.toLowerCase() === value.toLowerCase())) {
            return 'disparador.fileTypeTag';
        }
        if (filterTranslations.some(trans => trans.toLowerCase() === value.toLowerCase())) {
            return 'disparador.fileTypeFilter';
        }
        
        // Fallback para valores técnicos
        const typeMap = {
            'csv': 'disparador.fileTypeSpreadsheet',
            'tag': 'disparador.fileTypeTag',
            'filter': 'disparador.fileTypeFilter'
        };
        
        return typeMap[value] || null;
    };

    const StatCard = ({ label, value, color, icon: Icon }) => (
        <Box
            sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 'calc(50% - 8px)' },
                p: 2,
                borderRadius: 2.5,
                border: `2px solid ${color}20`,
                background: `linear-gradient(135deg, ${color}08 0%, ${color}04 100%)`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                    border: `2px solid ${color}40`,
                    boxShadow: `0 4px 12px ${color}20`,
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: `${color}15`,
                    color: color,
                }}
            >
                {Icon && <Icon sx={{ fontSize: 24 }} />}
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                </Typography>
                <Typography variant="h5" sx={{ color, fontWeight: 800, lineHeight: 1.1, mt: 0.3 }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );

    const InfoItem = ({ icon: Icon, label, value, fullWidth = false }) => (
        <Box
            sx={{
                flex: fullWidth ? '1 1 100%' : '1 1 auto',
                p: 1.75,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                border: '1px solid #e8f0f7',
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: '#eef5fb',
                    borderColor: '#d0e0ee',
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                {Icon && <Icon sx={{ fontSize: 18, color: '#7c8fa0' }} />}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' }, wordBreak: 'break-word', color: '#1a202c', fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        </Box>
    );

    return (
        <Modal 
            open={open} 
            onClose={onClose} 
            aria-labelledby="trigger-details-modal"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'transparent',
                    }
                }
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 'calc(100% - 24px)', sm: '90%', md: '600px' },
                    maxWidth: '600px',
                    maxHeight: { xs: '90vh', sm: '92vh' },
                    overflowY: 'auto',
                    borderRadius: 3.5,
                    bgcolor: '#ffffff',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    '-ms-overflow-style': 'none',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        p: { xs: 2, sm: 2.5 },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: { xs: '1.1rem', sm: '1.3rem' },
                            fontWeight: 700,
                            color: '#ffffff',
                            letterSpacing: '-0.5px',
                        }}
                    >
                        {t('disparador.detalhesDisparo')}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            borderRadius: '50%',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.3)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {/* Stats Section */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                        <StatCard
                            icon={CheckCircleIcon}
                            label={t('disparador.sucesso')}
                            value={success_count || 0}
                            color="#10b981"
                        />
                        <StatCard
                            icon={ErrorIcon}
                            label={t('disparador.falha')}
                            value={failure_count || 0}
                            color="#ef4444"
                        />
                    </Box>

                    <Divider sx={{ my: 2.5, opacity: 0.5 }} />

                    {/* Result Section */}
                    <InfoItem
                        icon={StorageIcon}
                        label={t('disparador.resultado')}
                        value={resulted !== null && resulted !== undefined ? resulted : t('disparador.aguardandoResultados')}
                        fullWidth
                    />

                    <Divider sx={{ my: 2.5, opacity: 0.5 }} />

                    {/* User Information Section */}
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            color: '#7c8fa0',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                            mb: 1.5,
                        }}
                    >
                        {t('disparador.informacoesGerais')}
                    </Typography>

                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                icon={PersonIcon}
                                label={t('disparador.table.responsible')}
                                value={responsibleName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                icon={EmailIcon}
                                label={t('disparador.email')}
                                value={email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                icon={SmartToyIcon}
                                label={t('disparador.botTriggered')}
                                value={triggeredBotName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                icon={BlockIcon}
                                label={t('disparador.interruptedBy')}
                                value={interruptedByName}
                            />
                        </Grid>
                    </Grid>

                    {/* File Input Type Section */}
                    {filter_file_type_input && (
                        <>
                            <Divider sx={{ my: 2.5, opacity: 0.5 }} />

                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    color: '#7c8fa0',
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.5px',
                                    mb: 1.5,
                                }}
                            >
                                {t('disparador.configuracoesBase')}
                            </Typography>

                            <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 1.75,
                                            borderRadius: 2,
                                            bgcolor: '#dbeafe',
                                            border: '1px solid #93c5fd',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1.5,
                                                background: '#2563eb',
                                                color: 'white',
                                            }}
                                        >
                                            <StorageIcon sx={{ fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#1e40af',
                                                    fontWeight: 700,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('disparador.fileInputType')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1a202c',
                                                    mt: 0.3,
                                                }}
                                            >
                                                {t(getFileInputTypeTranslationKey(filter_file_type_input) || 'disparador.fileTypeSpreadsheet')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                {(getFileInputTypeTranslationKey(filter_file_type_input) === 'disparador.fileTypeFilter') && filter_file_date_interaction && (
                                    <Grid item xs={12}>
                                        <InfoItem
                                            icon={CalendarTodayIcon}
                                            label={t('disparador.filterDateInteraction')}
                                            value={filter_file_date_interaction}
                                            fullWidth
                                        />
                                    </Grid>
                                )}

                                {(getFileInputTypeTranslationKey(filter_file_type_input) === 'disparador.fileTypeTag') && filter_file_tag_name && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                p: 1.75,
                                                borderRadius: 2,
                                                bgcolor: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.5,
                                                    background: '#3b82f6',
                                                    color: 'white',
                                                }}
                                            >
                                                <LabelIcon sx={{ fontSize: 20 }} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#1e40af',
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                >
                                                    {t('disparador.selectedTag')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#1a202c',
                                                        mt: 0.3,
                                                    }}
                                                >
                                                    {filter_file_tag_name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default TriggerDetailsModal;
